import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAudit } from '../context/AuditContext';
import { auditPath } from '../utils/auditRoutes';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, CheckCircle, Shield, Lock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { trackCheckoutStarted, trackCheckoutCompleted, trackBeginCheckout, trackAddPaymentInfo, trackPurchase } from '../utils/analytics';
import { AUDIT_PRICE_DOLLARS, AUDIT_PRICE_LABEL } from '../constants/product';
import { getApiUrl } from '../utils/apiBase';

const API = getApiUrl();
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function CheckoutForm({ clientSecret, sessionId, contactInfo, discountedAmount, promoCode }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message || 'Payment failed');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        await axios.post(`${API}/checkout/confirm-payment`, {
          payment_intent_id: paymentIntent.id,
          session_id: sessionId
        });

        // Track purchase with full e-commerce data
        trackPurchase(
          paymentIntent.id,
          contactInfo.company,
          contactInfo.industry,
          contactInfo.employeeCount,
          contactInfo.role,
          discountedAmount,
          promoCode
        );

        // Also track legacy event for compatibility
        trackCheckoutCompleted(
          contactInfo.company,
          contactInfo.industry,
          contactInfo.employeeCount,
          paymentIntent.amount
        );

        // Store session info
        localStorage.setItem('audit_session', JSON.stringify({
          session_id: sessionId,
          payment_intent_id: paymentIntent.id,
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          contact: contactInfo
        }));

        toast.success('Payment successful! Starting your audit...');
        navigate(auditPath('/cover', location.pathname));
      }
    } catch (err) {
      console.error('Payment error:', err);
      toast.error('An error occurred during payment');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      <Button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full py-6 text-base sm:text-lg font-semibold whitespace-normal break-words"
        data-testid="submit-payment-button"
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin flex-shrink-0" />
            <span className="break-words">Processing...</span>
          </>
        ) : (
          <>
            <Lock className="mr-2 h-5 w-5 flex-shrink-0" />
            <span className="break-words">Complete Payment & Start Audit (${discountedAmount})</span>
          </>
        )}
      </Button>
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Shield className="h-4 w-4" />
        <span>Secure payment powered by Stripe</span>
      </div>
    </form>
  );
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { updateContact, updateCompany } = useAudit();
  const testMode = searchParams.get('testmode') === 'true';
  const promoCode = searchParams.get('promo');
  const isEmergentPromo = promoCode === 'emergent';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [industry, setIndustry] = useState('');
  const [employeeCount, setEmployeeCount] = useState('');
  const [role, setRole] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(false);
  const [couponChecking, setCouponChecking] = useState(false);
  const [discountedAmount, setDiscountedAmount] = useState(AUDIT_PRICE_DOLLARS);
  const [clientSecret, setClientSecret] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Info, 2: Payment

  // Track checkout page view
  useEffect(() => {
    trackCheckoutStarted(testMode, isEmergentPromo ? 'emergent' : null);
    // Clear audit completion flag when starting/restarting audit
    localStorage.removeItem('audit_completed');
  }, [testMode, isEmergentPromo]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      return;
    }

    setCouponChecking(true);
    try {
      const response = await axios.post(`${API}/checkout/validate-coupon`, {
        coupon_code: couponCode.toUpperCase()
      });

      if (response.data.valid) {
        setCouponValid(true);
        setDiscountedAmount(response.data.final_amount / 100);
        toast.success('Coupon applied! Price: $' + (response.data.final_amount / 100));
      } else {
        toast.error('Invalid coupon code');
        setCouponValid(false);
      }
    } catch (error) {
      toast.error('Invalid coupon code');
      setCouponValid(false);
    } finally {
      setCouponChecking(false);
    }
  };

  const handleContinue = async (e) => {
    e.preventDefault();

    if (!firstName.trim()) {
      toast.error('Please enter your first name');
      return;
    }

    if (!lastName.trim()) {
      toast.error('Please enter your last name');
      return;
    }

    if (!email.trim() || !validateEmail(email)) {
      toast.error('Please enter a valid business email address');
      return;
    }

    if (!industry) {
      toast.error('Please select your industry');
      return;
    }

    if (!employeeCount) {
      toast.error('Please select number of employees');
      return;
    }

    if (!role) {
      toast.error('Please select your role');
      return;
    }

    setLoading(true);

    // Store contact info in context
    updateContact('name', `${firstName} ${lastName}`);
    updateContact('firstName', firstName);
    updateContact('lastName', lastName);
    updateContact('email', email);
    updateContact('industry', industry);
    updateContact('employeeCount', employeeCount);
    updateContact('role', role);
    updateCompany(company);

    try {
      // Use test endpoint if in test mode
      if (testMode) {
        const response = await axios.post(`${API}/checkout/create-test-session`, {
          name: `${firstName} ${lastName}`,
          firstName,
          lastName,
          company,
          email,
          industry,
          employeeCount,
          role
        });

        // Store test session
        localStorage.setItem('audit_session', JSON.stringify({
          session_id: response.data.session_id,
          payment_intent_id: 'test_bypass',
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          contact: { firstName, lastName, company, email, industry, employeeCount, role },
          test_mode: true
        }));

        toast.success('Test session created! Redirecting to audit...');
        setTimeout(() => navigate(auditPath('/cover', location.pathname)), 1000);
        return;
      }

      // Normal payment flow
      const response = await axios.post(`${API}/checkout/create-payment-intent`, {
        name: `${firstName} ${lastName}`,
        firstName,
        lastName,
        company,
        email,
        industry,
        employeeCount,
        role,
        coupon_code: couponValid ? couponCode.toUpperCase() : null,
        promo_code: isEmergentPromo ? 'emergent' : null
      });

      // Track begin checkout with e-commerce data
      trackBeginCheckout(company, industry, employeeCount);

      // If 100% off coupon (amount = 0), skip Stripe and go directly to audit
      if (response.data.amount === 0) {
        // Store contact info in context
        updateContact('name', `${firstName} ${lastName}`);
        updateContact('firstName', firstName);
        updateContact('lastName', lastName);
        updateContact('email', email);
        updateContact('industry', industry);
        updateContact('employeeCount', employeeCount);
        updateContact('role', role);
        updateCompany(company);

        // Store session
        localStorage.setItem('audit_session', JSON.stringify({
          session_id: response.data.session_id,
          payment_intent_id: 'coupon_free',
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          contact: { firstName, lastName, company, email, industry, employeeCount, role },
          coupon_applied: couponCode.toUpperCase()
        }));

        toast.success('Coupon applied! Redirecting to audit...');
        setTimeout(() => navigate(auditPath('/cover', location.pathname)), 1000);
        return;
      }

      // Regular payment flow
      setClientSecret(response.data.client_secret);
      setSessionId(response.data.session_id);
      setStep(2);

      // Track payment info step
      trackAddPaymentInfo('card');
    } catch (error) {
      console.error('Error creating payment intent:', error);
      toast.error(error.response?.data?.detail || 'Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#049aaa',
      colorBackground: '#ffffff',
      colorText: '#1c334a',
      colorDanger: '#df1b41',
      fontFamily: 'Roboto, system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/10 via-background to-primary/10">
      {/* Trust Banner - Full Width at Top */}
      {!testMode && (
        <div className="w-full bg-primary text-white py-3 text-center">
            <p className="font-bold text-sm sm:text-base">
            AI Opportunity Audit for experienced professional firms · {AUDIT_PRICE_LABEL}
          </p>
        </div>
      )}

      {/* Test Mode Banner */}
      {testMode && (
        <div className="w-full bg-orange-500 text-white py-3 text-center">
          <p className="font-bold">🧪 TEST MODE - No Payment Required</p>
        </div>
      )}

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="mb-6">
              <img
                src="/ai-audit-logo.png"
                alt="AI Opportunity Audit"
                className="h-28 w-auto mx-auto"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
            <h1 className="text-2xl sm:text-4xl font-extrabold text-primary mb-4 px-4" data-testid="checkout-title">
              You built the firm on trust. Do not adopt AI by guessing.
            </h1>

            {/* Price Pill */}
            <div className="inline-flex items-center gap-2 bg-accent rounded-full px-6 py-3 mb-6">
              <span className="text-secondary font-bold text-xl">{AUDIT_PRICE_LABEL} AI Opportunity Audit</span>
            </div>

            <p className="text-base text-foreground max-w-3xl mx-auto leading-relaxed">
              The <strong>AI Opportunity Audit™ — Digital Edition</strong> helps experienced owners identify one safe, useful AI workflow before client trust is put at risk. No hype, no guaranteed ROI claims, and no unsupervised client-advice automation. Just a clear first step for a professional firm.
            </p>
          </motion.div>

          {/* Value Props */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid sm:grid-cols-3 gap-4 mb-8"
          >
            <Card className="text-center">
              <CardContent className="pt-6">
                <CheckCircle className="h-8 w-8 text-primary mx-auto mb-2" />
                <p className="text-sm font-semibold">15 Minutes</p>
                <p className="text-xs text-muted-foreground">Fast, guided firm review</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <CheckCircle className="h-8 w-8 text-primary mx-auto mb-2" />
                <p className="text-sm font-semibold">First Safe Workflow</p>
                <p className="text-xs text-muted-foreground">Identify one responsible first pilot</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <CheckCircle className="h-8 w-8 text-primary mx-auto mb-2" />
                <p className="text-sm font-semibold">Trust Guardrails</p>
                <p className="text-xs text-muted-foreground">Know what not to automate yet</p>
              </CardContent>
            </Card>
          </motion.div>


          {/* Video Walkthrough */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
          >
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-secondary mb-4 text-center">See How the Audit Works</h2>
              <div className="relative rounded-lg overflow-hidden bg-black" style={{ paddingBottom: '56.25%', height: 0 }}>
                <video
                  className="absolute top-0 left-0 w-full h-full"
                  controls={false}
                  playsInline
                  preload="metadata"
                  id="walkthrough-video"
                  onEnded={(e) => {
                    e.target.currentTime = 0;
                    e.target.playbackRate = 2.0;
                    document.getElementById('play-button').style.display = 'flex';
                  }}
                  onPlay={() => {
                    document.getElementById('walkthrough-video').playbackRate = 2.0;
                  }}
                >
                  <source src="/audit-walkthrough.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <button
                  onClick={(e) => {
                    const video = document.getElementById('walkthrough-video');
                    const button = document.getElementById('play-button');
                    if (video.paused) {
                      video.playbackRate = 2.0;
                      video.play();
                      button.style.display = 'none';
                    }
                  }}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/20 transition-colors group"
                  id="play-button"
                  aria-label="Play video"
                >
                  <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-10 h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </button>
              </div>
              <p className="text-sm text-muted-foreground text-center mt-3">
                Generate a practical first-step report in less than 15 minutes.
              </p>
            </div>
          </motion.div>


          {/* Checkout Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="shadow-lg">
              <CardHeader className="text-center pt-10 pb-6">
                <CardTitle className="text-4xl font-extrabold mb-2">
                  {step === 1 ? 'Get Started Today' : 'Secure Payment'}
                </CardTitle>
                <CardDescription className="text-base w-full">
                  {step === 1
                    ? 'Tell us a bit about your firm to get started. Your AI Opportunity Report will generate after you complete the audit.'
                    : `Complete your $${discountedAmount} payment to access the audit`}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-8 pb-10">
                {step === 1 ? (
                  <form onSubmit={handleContinue} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="John"
                          required
                          data-testid="checkout-firstname-input"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Doe"
                          required
                          data-testid="checkout-lastname-input"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Your Company"
                        data-testid="checkout-company-input"
                        className="mt-1"
                      />
                    </div>
                    <div className="mb-6">
                      <Label htmlFor="email">Business Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        required
                        data-testid="checkout-email-input"
                        className="mt-1"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="industry">Industry *</Label>
                        <Select value={industry} onValueChange={setIndustry}>
                          <SelectTrigger className="mt-1" data-testid="checkout-industry-select">
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accounting-bookkeeping">Accounting / Bookkeeping</SelectItem>
                            <SelectItem value="consulting-advisory">Consulting / Advisory</SelectItem>
                            <SelectItem value="insurance-agency">Insurance Agency</SelectItem>
                            <SelectItem value="financial-advisory">Financial Planning / Wealth Advisory</SelectItem>
                            <SelectItem value="fractional-cfo-ops">Fractional CFO / Operations</SelectItem>
                            <SelectItem value="professional-services">Other Professional Services</SelectItem>
                            <SelectItem value="manufacturing">Manufacturing</SelectItem>
                            <SelectItem value="retail">Retail / E-commerce</SelectItem>
                            <SelectItem value="healthcare">Healthcare</SelectItem>
                            <SelectItem value="technology">Technology / SaaS</SelectItem>
                            <SelectItem value="finance">Finance / Insurance</SelectItem>
                            <SelectItem value="real-estate">Real Estate</SelectItem>
                            <SelectItem value="hospitality">Hospitality / Food Service</SelectItem>
                            <SelectItem value="construction">Construction</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="employeeCount">Number of Employees *</Label>
                        <Select value={employeeCount} onValueChange={setEmployeeCount}>
                          <SelectTrigger className="mt-1" data-testid="checkout-employees-select">
                            <SelectValue placeholder="Select range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-10">1-10</SelectItem>
                            <SelectItem value="11-25">11-25</SelectItem>
                            <SelectItem value="26-50">26-50</SelectItem>
                            <SelectItem value="51-100">51-100</SelectItem>
                            <SelectItem value="101-250">101-250</SelectItem>
                            <SelectItem value="251-500">251-500</SelectItem>
                            <SelectItem value="500+">500+</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="role">Your Role *</Label>
                      <Select value={role} onValueChange={setRole}>
                        <SelectTrigger className="mt-1" data-testid="checkout-role-select">
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="owner">Owner / Founder</SelectItem>
                          <SelectItem value="ceo">CEO / President</SelectItem>
                          <SelectItem value="coo">COO / Operations Director</SelectItem>
                          <SelectItem value="cto">CTO / Technology Director</SelectItem>
                          <SelectItem value="cfo">CFO / Finance Director</SelectItem>
                          <SelectItem value="vp">VP / Department Head</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="consultant">Consultant / Advisor</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>


                    {/* Coupon Code - Hidden for Emergent promo */}
                    {!isEmergentPromo && (
                      <div className="border-t border-muted pt-4 mt-2">
                        <Label htmlFor="coupon">Have a Coupon Code?</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            id="coupon"
                            type="text"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            placeholder="Enter code"
                            data-testid="coupon-input"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleApplyCoupon}
                            disabled={!couponCode || couponChecking}
                            data-testid="apply-coupon-button"
                          >
                            {couponChecking ? 'Checking...' : 'Apply'}
                          </Button>
                        </div>
                        {couponValid && (
                          <p className="text-sm text-accent font-semibold mt-2">
                            ✓ Coupon applied! Price: ${discountedAmount}
                          </p>
                        )}
                      </div>
                    )}


                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full py-6 text-base sm:text-lg font-semibold mt-6 px-4 whitespace-normal break-words"
                      data-testid="continue-to-payment-button"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <span className="text-center">
                          {couponValid && discountedAmount === 0
                            ? 'Start FREE with Coupon → Instant Access'
                            : couponValid
                              ? `Start for $${discountedAmount} → Instant Access`
                              : `Start for $${discountedAmount} → Instant Access`
                          }
                        </span>
                      )}
                    </Button>
                  </form>
                ) : (
                  clientSecret && (
                    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
                      <CheckoutForm
                        clientSecret={clientSecret}
                        sessionId={sessionId}
                        contactInfo={{ firstName, lastName, company, email, industry, employeeCount, role }}
                        discountedAmount={discountedAmount}
                        promoCode={isEmergentPromo ? 'emergent' : null}
                      />
                    </Elements>
                  )
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Trust Signals */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 text-center space-y-3"
          >
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span>Secure Checkout • Powered by</span>
              <svg className="h-4" viewBox="0 0 60 25" fill="currentColor">
                <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 01-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 013.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 01-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 01-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 00-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z"/>
              </svg>
            </div>

            <p className="text-sm text-muted-foreground mt-4">
              <strong className="text-secondary">One-time payment. No subscriptions.</strong>
            </p>

            <p className="text-xs text-muted-foreground mt-4">
              <strong className="text-secondary">Trilogy One Consulting</strong> • Clarity. Momentum. Resilience.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}